import React, { useCallback } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button/Button';
import { partial, uniqueId } from 'lodash';
import PropTypes from 'prop-types';

import { cxHelpers } from 'util/className';
import { moment } from 'util/dateTime';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';

import IndividualBreak from './IndividualBreak';
import MissingPaidBreaks from './MissingPaidBreaks';

const { cx, cxEl } = cxHelpers('TimebreakRows');

const TimebreakRows = ({
  addTimebreak,
  dateStr,
  isMobileScreenSize,
  onChangeTimebreak,
  removeTimebreak,
  shiftEndAt,
  shiftStartAt,
  mandatedBreaks,
  paid,
  breaks,
  errors,
  employeeName,
  setChangesMade,
}) => {
  const handleAddTimebreak = useCallback(
    mandatedBreakId => {
      trackUxEvent({
        actionType: TRACK_ACTION_TYPES.CLICK,
        productArea: PRODUCT_AREAS.TIMESHEETS,
        eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
        eventAction: EVENT_ACTIONS.ADD_BREAK_CLICKED_V3,
      });

      addTimebreak({
        key: uniqueId('NEW-TIMEBREAK'),
        paid,
        mandated_break_id: mandatedBreakId,
      });
    },
    [addTimebreak, paid]
  );

  const activeMandatedBreaks = mandatedBreaks.filter(mb => mb.get('active'));
  const isWaived = timebreak => timebreak.get('employee_waived');

  return (
    <Box className={cx()}>
      {breaks.filter(timebreak => !timebreak.get('remove')).isEmpty()
        ? !activeMandatedBreaks.isEmpty()
        : breaks.map((timebreak, i) => {
            const id = timebreak.get('id') || timebreak.get('key');
            const errorsObject = errors.length > 0 ? errors[i] : null;

            return (
              <IndividualBreak
                key={i}
                timebreakId={id}
                mandatedBreakId={timebreak.get('mandated_break_id')}
                startAt={
                  timebreak.get('start_at') && moment(timebreak.get('start_at'))
                }
                endAt={
                  timebreak.get('end_at') && moment(timebreak.get('end_at'))
                }
                isMobileScreenSize={isMobileScreenSize}
                mandatedBreaks={mandatedBreaks}
                onChangeTimebreak={partial(onChangeTimebreak, id)}
                removeTimebreak={partial(removeTimebreak, id)}
                dateStr={dateStr}
                shiftStartAt={shiftStartAt}
                shiftEndAt={shiftEndAt}
                isWaived={isWaived(timebreak)}
                validationErrors={errorsObject?.errors || []}
                remove={timebreak.get('remove')}
                paidTimebreak={timebreak.get('paid')}
                employeeName={employeeName}
                setChangesMade={setChangesMade}
              />
            );
          })}

      {activeMandatedBreaks.isEmpty() ? (
        <MissingPaidBreaks paid={paid} isMobile={isMobileScreenSize} />
      ) : (
        <Button
          className={cxEl('add-break')}
          onClick={partial(
            handleAddTimebreak,
            mandatedBreaks.get('0').get('id')
          )}
          size="small"
          startIcon="Add"
          variant="tertiary"
        >
          <Text i18n="timecard_modal_v3.timebreaks_section.add_break" />
        </Button>
      )}
    </Box>
  );
};

TimebreakRows.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  dateStr: PropTypes.string,
  isMobileScreenSize: PropTypes.bool,
  paid: PropTypes.bool,
  mandatedBreaks: ImmutablePropTypes.list,
  onChangeTimebreak: PropTypes.func.isRequired,
  removeTimebreak: PropTypes.func.isRequired,
  shiftEndAt: PropTypes.object,
  shiftStartAt: PropTypes.object,
  breaks: ImmutablePropTypes.list,
  errors: PropTypes.array,
  employeeName: PropTypes.string,
  setChangesMade: PropTypes.func.isRequired,
};

export default TimebreakRows;
